import { z } from "zod";
import { userDtoSchema } from "./user.js";
import { membershipDtoSchema } from "./membership.js";

/**
 * The authenticated caller's own view of themselves — `GET /api/auth/me`.
 * `isPlatformAdmin` lives here, not on `UserDto`, because it's a fact about
 * this session's privileges, not a public fact about the user as seen by
 * other org members.
 */
export const meDtoSchema = z.object({
  user: userDtoSchema,
  isPlatformAdmin: z.boolean(),
  memberships: z.array(membershipDtoSchema),
});

export type MeDto = z.infer<typeof meDtoSchema>;

export const requestCodeResponseSchema = z.object({ ok: z.literal(true) });
export type RequestCodeResponse = z.infer<typeof requestCodeResponseSchema>;

export const requestCodeBodySchema = z.object({ email: z.string().email() });
export type RequestCodeBody = z.infer<typeof requestCodeBodySchema>;

export const verifyCodeBodySchema = z.object({
  email: z.string().email(),
  code: z.string().length(6).regex(/^\d{6}$/),
});
export type VerifyCodeBody = z.infer<typeof verifyCodeBodySchema>;

export const verifyCodeResponseSchema = z.object({ ok: z.literal(true), user: userDtoSchema });
export type VerifyCodeResponse = z.infer<typeof verifyCodeResponseSchema>;

// --- Passkeys ------------------------------------------------------------
// The WebAuthn payloads themselves (options out, credential back) are shaped
// by the spec and validated by @simplewebauthn on the server, so they cross
// the wire untyped here; only the envelopes around them are ours.

export const passkeyDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string().nullable(),
  backedUp: z.boolean(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
});
export type PasskeyDto = z.infer<typeof passkeyDtoSchema>;

export const renamePasskeyBodySchema = z.object({ name: z.string().trim().min(1).max(60) });
export type RenamePasskeyBody = z.infer<typeof renamePasskeyBodySchema>;

export const passkeyRegistrationBodySchema = z.object({ response: z.record(z.unknown()) });
export type PasskeyRegistrationBody = z.infer<typeof passkeyRegistrationBodySchema>;

export const passkeyAuthenticationBodySchema = z.object({
  challengeId: z.string().min(1).max(64),
  response: z.record(z.unknown()),
});
export type PasskeyAuthenticationBody = z.infer<typeof passkeyAuthenticationBodySchema>;
